import { styled } from '@mui/material'
import type { FC } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

const Container = styled('div')({
  width: '100%',
  // height: '100%',  <- remove this
  minHeight: '100vh', // optional, ensures it at least fills the viewport
  color: '#000',
  background: '#EFF3FD',
  padding: '2rem',
})

const faqSections = [
  {
    title: 'General Information',
    items: [
      {
        question: 'What is 3S Maintenance Portal?',
        answer:
          'A digital or online platform used by DND, Dealers, and Purchasing to monitor and complete minor and major maintenance works across Yamaha 3S Shops nationwide.',
      },
      {
        question: 'How do I log in/register?',
        answer:
          'Register: Visit https://3sportal.yamahamotorphilippines.com.ph/index, fill out all required fields, and submit your registration form.\nLogin: Use your registered username and password on the same portal login page.',
      },
      {
        question: 'What are the system requirements to use the portal?',
        answer:
          'Supported browsers: Latest versions of Chrome, Firefox, Edge, and Safari.\nOther: Stable internet connection is recommended for best performance.',
      },
      {
        question: "What are the portal's operating hours?",
        answer: 'The portal is typically accessible 24/7.',
      },
      {
        question: 'Where is the portal located (if applicable)?',
        answer:
          'It can be accessed through a shared link, which redirects you to the 3S Maintenance Portal page.',
      },
      {
        question: 'Is there a mobile app?',
        answer:
          'No dedicated mobile app, but the portal can be accessed via a mobile browser.',
      },
    ],
  },
  {
    title: 'Functionality and Features',
    items: [
      {
        question:
          'How do I...? (e.g., upload a document, submit a ticket, etc.)',
        answer:
          'Log in as an Area Controller → Go to the 3S Maintenance System → Click "File New Ticket" → Fill out all necessary fields → Submit.',
      },
      {
        question: 'What are the key features of the 3S Maintenance Portal?',
        answer:
          'User-Friendly Interface: Intuitive, easy to use, with clear navigation and responsive design.\nSecure Access: SSO, role-based access control, and encryption for user data.\nContent Management: Manage approvals, revisions, rejections, and cancellations of maintenance tickets.',
      },
      {
        question: 'How do I navigate the 3S Maintenance portal?',
        answer:
          'Visit https://3sportal.yamahamotorphilippines.com.ph/index → Log in → Navigate to the System Portal → Click 3S Maintenance System to access features.',
      },
      {
        question: 'How do I customize my profile?',
        answer:
          'In the 3Sportal, go to Profile → My Profile → Edit, update your information, then click Submit.',
      },
      {
        question: 'What are the supported file types?',
        answer:
          'The DND 3S Maintenance portal supports attachments in PDF, Word, JPEG, and PNG file formats.',
      },
      {
        question:
          'How do I search for maintenance ticket information within the portal?',
        answer:
          'Go to the Maintenance Ticket page, then type keywords into the search bar.',
      },
    ],
  },
  {
    title: 'Policies and Procedures',
    items: [
      {
        question: 'How do I report a problem or bug?',
        answer: '(FOR DISCUSSION – STILL NO FEATURE)',
      },
      {
        question: 'How do I request support?',
        answer: '(FOR DISCUSSION – STILL NO FEATURE)',
      },
    ],
  },
  {
    title: 'Troubleshooting',
    items: [
      {
        question: "I'm having trouble logging in. What should I do?",
        answer: 'Contact the DND team or YMPH IT team for assistance.',
      },
      {
        question: "My document didn't upload. What could be the problem?",
        answer:
          'Check your internet connection, file format, and file size. Unsupported formats or oversized files will fail to upload.',
      },
      {
        question: 'Why is the 3S Maintenance portal slow?',
        answer:
          'Possible reasons include a weak internet connection, high server traffic, or multiple users accessing the system simultaneously.',
      },
      {
        question: 'How do I reset my password?',
        answer:
          'On the login page, click "Forgot Password" → Enter your registered email → Enter the OTP code sent to you → Create a new password.',
      },
    ],
  },
  {
    title: 'Other Useful Information',
    items: [
      {
        question: 'What are the benefits of using the 3S Maintenance portal?',
        answer:
          'Centralized Information – Transparency of ongoing maintenance tickets.\nEnhanced Productivity – Streamlined workflows and quick access to tools.\nImproved Accessibility – Convenient access to FAQs, guides, and real-time ticket updates.',
      },
    ],
  },
]

interface FaqProps {}

export const Faq: FC<FaqProps> = () => {
  return (
    <Container>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Frequently Asked Questions (FAQs) for DND Web App
      </Typography>

      <Typography
        variant="body1"
        sx={{ color: 'text.secondary', maxWidth: '800px' }}
        gutterBottom
      >
        Here you’ll find answers to the most common questions about using the
        DND 3S Maintenance Portal.
      </Typography>

      {faqSections.map((section, sIndex) => (
        <div
          key={sIndex}
          style={{ marginBottom: '2rem', marginTop: sIndex === 0 ? '2rem' : 0 }}
        >
          <Typography
            variant="h5"
            fontWeight={600}
            gutterBottom
            sx={{ color: '#1A1A1A' }}
          >
            {section.title}
          </Typography>

          {section.items.map((item, index) => (
            <Accordion key={item.question}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={500}>
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">{item.answer || 'N/A'}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      ))}
    </Container>
  )
}
